import React, { useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import api from "../../services/api";
import { getLiveSocket } from "../../services/binSocket";
import { formatDistance, formatMinutes } from "./phone";

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (!document.getElementById("leaflet-css")) {
    const css = document.createElement("link");
    css.id = "leaflet-css";
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);
  }
  return new Promise((resolve, reject) => {
    const existing = document.getElementById("leaflet-js");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.L));
      return;
    }
    const script = document.createElement("script");
    script.id = "leaflet-js";
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function movedEnough(previous, coords) {
  if (!previous) return true;
  const lat = ((coords.latitude - previous.lat) * Math.PI) / 180;
  const lng = ((coords.longitude - previous.lng) * Math.PI) / 180;
  const a = Math.sin(lat / 2) ** 2
    + Math.cos((previous.lat * Math.PI) / 180)
    * Math.cos((coords.latitude * Math.PI) / 180)
    * Math.sin(lng / 2) ** 2;
  const meters = 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return meters >= 12 || Date.now() - previous.at > 8000;
}

export default function MeetModal({ binId, onClose }) {
  const mapNode = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const fittedRef = useRef(false);
  const namesRef = useRef({ seller: "Seller", agent: "Agent" });
  const watchRef = useRef(null);
  const lastSent = useRef(null);
  const [info, setInfo] = useState(null);
  const [route, setRoute] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const draw = async (meet, nextRoute, names, forceFit) => {
    const L = await loadLeaflet();
    if (!mapRef.current && mapNode.current) {
      mapRef.current = L.map(mapNode.current, { zoomControl: true }).setView([6.5244, 3.3792], 12);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap",
      }).addTo(mapRef.current);
      setTimeout(() => mapRef.current?.invalidateSize(), 200);
    }
    if (!mapRef.current) return;
    if (layerRef.current) layerRef.current.remove();
    const group = L.layerGroup().addTo(mapRef.current);
    layerRef.current = group;
    const points = [];
    const pin = (point, color, name) => {
      if (!point?.lat) return;
      points.push([point.lat, point.lng]);
      const icon = L.divIcon({
        className: "",
        iconSize: [0, 0],
        html: `<div style="display:flex;flex-direction:column;align-items:center;transform:translate(-50%,-100%);pointer-events:none">
          <div style="background:#111827;color:#fff;font:600 12px/1.2 sans-serif;padding:4px 8px;border-radius:999px;margin-bottom:4px;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.25)">${escapeHtml(name)}</div>
          <div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>
        </div>`,
      });
      L.marker([point.lat, point.lng], { icon }).addTo(group);
    };
    pin(meet?.seller, "#15803d", names?.seller || "Seller");
    pin(meet?.agent, "#1d4ed8", names?.agent || "Agent");
    const line = nextRoute?.geometry?.coordinates?.map(([lng, lat]) => [lat, lng]) || [];
    if (line.length) {
      L.polyline(line, { color: "#ffffff", weight: 8, opacity: 0.95 }).addTo(group);
      L.polyline(line, { color: "#15803d", weight: 5 }).addTo(group);
    }
    const fit = line.length ? line : points;
    if (fit.length && (forceFit || !fittedRef.current)) {
      mapRef.current.fitBounds(L.latLngBounds(fit), { padding: [32, 32], maxZoom: 16 });
      fittedRef.current = true;
    }
  };

  useEffect(() => {
    namesRef.current = {
      seller: info?.seller?.name || "Seller",
      agent: info?.agent?.name || "Agent",
    };
  }, [info]);

  useEffect(() => {
    let stop = false;
    api.get(`/meet/${binId}`).then((response) => {
      if (stop) return;
      setInfo(response.data.data);
      setRoute(response.data.data.route);
      const data = response.data.data;
      draw(data.meet, data.route, {
        seller: data.seller?.name || "Seller",
        agent: data.agent?.name || "Agent",
      }).catch(() => setError("The map could not be drawn."));
    }).catch(() => setError("This meeting could not be opened."));
    const socket = getLiveSocket();
    socket?.emit("meet:join", { binId });
    const onLocation = async () => {
      const response = await api.get(`/meet/${binId}`);
      if (stop) return;
      setInfo(response.data.data);
      setRoute(response.data.data.route);
      draw(response.data.data.meet, response.data.data.route, namesRef.current).catch(() => {});
    };
    const onMoving = (payload) => {
      if (String(payload.binId) === String(binId)) setNotice(`${payload.name} started moving.`);
    };
    socket?.on("meet:location", onLocation);
    socket?.on("meet:moving", onMoving);

    const pushLocation = (coords) => {
      if (!movedEnough(lastSent.current, coords)) return;
      lastSent.current = { lat: coords.latitude, lng: coords.longitude, at: Date.now() };
      api.post(`/meet/${binId}/location`, {
        lat: coords.latitude,
        lng: coords.longitude,
        moving: true,
      }).then((response) => {
        if (stop) return;
        setRoute(response.data.data.route);
        setInfo((current) => ({
          ...(current || {}),
          meet: response.data.data.meet,
        }));
        draw(response.data.data.meet, response.data.data.route, namesRef.current).catch(() => {});
      }).catch(() => setError("Location was not shared."));
    };

    if (navigator.geolocation) {
      watchRef.current = navigator.geolocation.watchPosition(
        (position) => {
          setSharing(true);
          pushLocation(position.coords);
        },
        () => setError("Allow location so the route uses this device."),
        { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 }
      );
    } else {
      setError("This browser cannot share location.");
    }

    return () => {
      stop = true;
      socket?.off("meet:location", onLocation);
      socket?.off("meet:moving", onMoving);
      if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
      mapRef.current?.remove();
      mapRef.current = null;
      fittedRef.current = false;
    };
  }, [binId]);

  const mapsLink = () => {
    const seller = info?.meet?.seller;
    const agent = info?.meet?.agent;
    if (!seller?.lat || !agent?.lat) return "";
    const role = info.role;
    const origin = role === "seller" ? seller : agent;
    const destination = role === "seller" ? agent : seller;
    return `https://www.google.com/maps/dir/?api=1&origin=${origin.lat},${origin.lng}&destination=${destination.lat},${destination.lng}&travelmode=driving`;
  };

  const fitRoute = () => {
    fittedRef.current = false;
    draw(info?.meet, route, namesRef.current, true).catch(() => {});
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-3 sm:items-center sm:p-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-start justify-between gap-3 bg-green-800 px-4 py-4 text-white sm:px-5">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-green-100">Meet</p>
            <h2 className="text-xl font-bold">Route to each other</h2>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 cursor-pointer rounded-full bg-white/10 p-2">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div ref={mapNode} className="h-56 w-full bg-gray-100 sm:h-72 lg:h-80" />
          <div className="space-y-3 p-4 pb-6 sm:p-5">
            {error && <p className="rounded-2xl bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
            {notice && <p className="rounded-2xl bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}
            <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2">
              <div className="min-w-0 rounded-2xl bg-gray-50 p-3">
                <p className="text-xs text-gray-500">Time</p>
                <p className="break-words text-lg font-bold text-gray-900">{route ? formatMinutes(route.durationSeconds) : "Waiting for both locations"}</p>
              </div>
              <div className="min-w-0 rounded-2xl bg-gray-50 p-3">
                <p className="text-xs text-gray-500">Distance</p>
                <p className="break-words text-lg font-bold text-gray-900">{route ? formatDistance(route.distanceMeters) : "—"}</p>
              </div>
            </div>
            <p className="text-sm text-gray-500">
              {sharing
                ? "This device's location is on the map. Zoom and pan stay on your screen only."
                : "Waiting for this device's location. Green is the seller. Blue is the agent."}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <button type="button" onClick={fitRoute} className="cursor-pointer rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-800 sm:py-2">
                Fit route
              </button>
              {mapsLink() && (
                <a href={mapsLink()} target="_blank" rel="noreferrer" className="rounded-xl bg-green-700 px-4 py-2.5 text-center text-sm font-semibold text-white sm:py-2">
                  Open turn-by-turn
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
